/**
 * Publik sahifalar tartibi: xizmat lentasi + sarlavha (menyu) + kontent + futer.
 * Barcha publik sahifalarda bir xil; sahifalar almashganda bu karkas o'zgarmaydi.
 *
 * Menyu sarlavhada. «Bosh sahifa» — bosh sahifaning yuqori qismiga, «Yangiliklar» —
 * bosh sahifadagi yangiliklar blokiga silliq o'tkazadi (boshqa sahifadan bosilsa,
 * bosh sahifa ochilib shu blokka tushiriladi). Qolgan modullar o'z sahifasida ochiladi.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { homeRoute, useAuth } from "@/app/AuthContext";

import type { PortalMenuItem } from "@/shared/api/types";
import { HEADER_VISIBLE, INFO_NAV, isEntryActive, PRIMARY_NAV } from "@/shared/data/publicNav";
import type { PublicNavEntry } from "@/shared/data/publicNav";
import { usePortalConfig, usePortalHome } from "@/shared/data/portal";
import { useI18n } from "@/shared/i18n";
import { scrollHome } from "@/shared/lib/homeScroll";
import type { HomeSpot } from "@/shared/lib/homeScroll";
import { useScrollProgress } from "@/shared/lib/motion";
import { MARKET_ANCHOR, NEWS_ANCHOR, sectionHref } from "@/shared/lib/sectionRoutes";
import { AiAssistant } from "@/shared/ui/AiAssistant";
import { A11yButton, HeaderClock, LangMenu } from "@/shared/ui/A11yPanel";
import { BrandMark, Icon, isIconName } from "@/shared/ui/Icon";
import { MobileLinkButton } from "@/shared/ui/MobileLinkButton";

import { PublicFooter } from "./PublicFooter";

/** Menyu tuguni: kodda belgilangan modul yoki superadmin «Portal menyusi»dan kelgan qo'shimcha band. */
interface NavNode {
  key: string;
  label: string;
  href: string;
  icon?: string;
  newTab?: boolean;
  /** Ishga tushmagan modul — «Tez kunda» belgisi. */
  soon?: boolean;
  /** Bosh sahifa ichida silliq o'tadigan band. */
  home?: HomeSpot;
  active?: boolean;
  children: NavNode[];
}

/**
 * Superadmin «Portal menyusi»dan kelgan bandlardan menyuda hali bo'lmaganlari
 * (masalan API hujjatlari, data.gov.uz) «Yana» ro'yxatiga qo'shiladi.
 */
function menuExtras(items: PortalMenuItem[], covered: Set<string>): NavNode[] {
  const flat = (nodes: PortalMenuItem[]): PortalMenuItem[] =>
    nodes.flatMap((node) => [
      ...(node.href && node.href !== "#" ? [node] : []),
      ...flat(node.children ?? []),
    ]);
  const seen = new Set<string>();
  const result: NavNode[] = [];
  for (const item of flat(items)) {
    const target = sectionHref(item.href);
    if (target.startsWith("/api/") || covered.has(target) || seen.has(target)) continue; // API hujjatlari ko'rsatilmaydi
    seen.add(target);
    result.push({
      key: `cms-${item.id}`,
      label: item.title,
      href: target,
      icon: item.icon,
      newTab: item.open_in_new_tab,
      children: [],
    });
  }
  return result;
}

/**
 * Menyu havolasi: `/…` — ilova ichidagi marshrut, qolgani (http, `/api/…`) — oddiy havola.
 * Bosh sahifadagi bandlar (`home`) bosh sahifada turganda sahifani yangilamasdan silliq suradi.
 */
function NavLinkItem({
  node,
  onNavigate,
  className,
}: {
  node: NavNode;
  onNavigate?: () => void;
  className?: string;
}) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const content = (
    <>
      {node.icon && isIconName(node.icon) ? <Icon name={node.icon} size={18} /> : null}
      {node.label}
      {node.soon ? <span className="soon-badge">{t("nav.soon")}</span> : null}
    </>
  );
  const common = {
    className: [className, node.active ? "is-active" : ""].filter(Boolean).join(" ") || undefined,
    "aria-current": node.active ? ("page" as const) : undefined,
  };

  if (node.href.startsWith("/") && !node.href.startsWith("/api/")) {
    const onClick = (event: MouseEvent) => {
      onNavigate?.();
      if (!node.home || pathname !== "/") return; // boshqa sahifadan — oddiy marshrut
      event.preventDefault();
      scrollHome(node.home);
      navigate(node.home === "news" ? { pathname: "/", hash: `#${NEWS_ANCHOR}` } : "/", {
        replace: true,
      });
    };
    return (
      <Link to={node.href} {...common} onClick={onClick}>
        {content}
      </Link>
    );
  }
  return (
    <a
      href={node.href}
      {...common}
      onClick={onNavigate}
      {...(node.newTab || /^https?:/.test(node.href)
        ? { target: "_blank", rel: "noreferrer noopener" }
        : {})}
    >
      {content}
    </a>
  );
}

/** Ochiladigan menyu: tugma + ro'yxat; tashqarisi bosilganda yoki Esc bilan yopiladi. */
function DropdownMenu({ node }: { node: NavNode }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const listId = `nav-drop-${node.key}`;
  const active = node.children.some((child) => child.active);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        root.current?.querySelector<HTMLButtonElement>("button")?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="nav-more" ref={root}>
      <button
        type="button"
        className={`nav-more-btn${active ? " is-active" : ""}`}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
      >
        {node.label}
        <Icon name="chevronDown" size={14} className={open ? "flip-y" : undefined} />
      </button>
      {open ? (
        <div className="nav-more-list" id={listId}>
          <ul className="nav-sublist">
            {node.children.map((child) => (
              <li key={child.key}>
                <NavLinkItem node={child} onNavigate={() => setOpen(false)} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Mobil menyuda daraxt tekis ko'rsatiladi. */
function MobileNavItems({ nodes, onNavigate }: { nodes: NavNode[]; onNavigate: () => void }) {
  return (
    <>
      {nodes.map((node) => (
        <div key={node.key} className="mobile-nav-group">
          {node.children.length ? (
            <MobileNavItems nodes={node.children} onNavigate={onNavigate} />
          ) : (
            <NavLinkItem node={node} onNavigate={onNavigate} />
          )}
        </div>
      ))}
    </>
  );
}

/**
 * Kirish tugmasi: kirmagan bo'lsa — `/kirish`; allaqachon kirgan bo'lsa tugma
 * joyida qoladi, lekin login so'ramasdan to'g'ridan-to'g'ri kabinetga olib o'tadi.
 */
export function useEnterLink() {
  const { user, status } = useAuth();
  const loggedIn = status === "authenticated" && Boolean(user);
  return { loggedIn, user, to: loggedIn ? homeRoute(user) : "/kirish" };
}

/**
 * Marshrut almashganda sahifa tepasiga qaytaradi; manzilda `#blok` bo'lsa —
 * (bosh sahifa chizilgach) shu blokka silliq tushiradi.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const previous = useRef(pathname);

  useEffect(() => {
    const samePage = previous.current === pathname;
    previous.current = pathname;
    if (!samePage) window.scrollTo({ top: 0, behavior: "instant" });

    if (hash) {
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    if (samePage) window.scrollTo({ top: 0, behavior: "smooth" });
    return undefined;
  }, [pathname, hash]);

  return null;
}

/**
 * Bosh sahifada menyuning qaysi bandi yonishini aniqlaydi: yangiliklar bloki
 * ekran o'rtasidan yuqorida boshlangan va undan keyingi blok (savdo) hali
 * boshlanmagan bo'lsa — «Yangiliklar», aks holda — «Bosh sahifa».
 */
function useHomeSpot(enabled: boolean): HomeSpot {
  const [spot, setSpot] = useState<HomeSpot>("top");

  useEffect(() => {
    if (!enabled) {
      setSpot("top");
      return undefined;
    }
    let frame = 0;
    const measure = () => {
      frame = 0;
      const news = document.getElementById(NEWS_ANCHOR);
      if (!news) return;
      const line = window.innerHeight * 0.4;
      const next = document.getElementById(MARKET_ANCHOR);
      const inNews =
        news.getBoundingClientRect().top <= line &&
        (!next || next.getBoundingClientRect().top > line);
      setSpot(inNews ? "news" : "top");
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return spot;
}

export function PublicLayout({ children }: { children?: ReactNode }) {
  const { t } = useI18n();
  const enter = useEnterLink();
  const { pathname } = useLocation();
  // Rasmiy rekvizitlar CMS'dan (`PortalSettings`), backendsiz — zaxira qiymat.
  const config = usePortalConfig();
  const home = usePortalHome();
  const [menuOpen, setMenuOpen] = useState(false);
  const progress = useScrollProgress();
  const homeSpot = useHomeSpot(pathname === "/");

  /* Sarlavhadagi menyu: asosiy bandlar yonma-yon, qolganlari «Yana» ro'yxatida. */
  const headerNav = useMemo<NavNode[]>(() => {
    const node = (entry: PublicNavEntry): NavNode => ({
      key: entry.key,
      label: t(entry.label === "nav.appealSend" ? "nav.appeal" : entry.label),
      href: entry.to,
      soon: entry.status === "soon",
      home: entry.home,
      active: isEntryActive(entry, pathname, homeSpot),
      children: [],
    });
    const all = [...PRIMARY_NAV, ...INFO_NAV];
    const visible = HEADER_VISIBLE.map((key) => all.find((entry) => entry.key === key)).filter(
      (entry): entry is PublicNavEntry => Boolean(entry),
    );
    const rest = all.filter((entry) => !HEADER_VISIBLE.includes(entry.key));
    // «Yana» ichida to'liq nomlar turadi (qisqartirilgan «Murojaat» emas).
    const moreNode = (entry: PublicNavEntry): NavNode => ({ ...node(entry), label: t(entry.label) });

    const covered = new Set<string>(["/", ...all.map((entry) => entry.to)]);
    const extras = menuExtras(
      (home?.menu ?? []).filter((item) => (item.location ?? "header") === "header"),
      covered,
    );

    return [
      ...visible.map(node),
      {
        key: "more",
        label: t("nav.more"),
        href: "#",
        children: [...rest.map(moreNode), ...extras],
      },
    ];
  }, [home, t, pathname, homeSpot]);

  const assistantPages = useMemo(
    () =>
      [...PRIMARY_NAV, ...INFO_NAV]
        .filter((entry) => entry.to.startsWith("/") && !entry.to.includes("#"))
        .map((entry) => ({ path: entry.to, label: t(entry.label) })),
    [t],
  );

  /* Sahifa almashganda mobil menyu yopiladi. */
  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <div className="public-shell">
      <a className="skip-link" href="#main">
        {t("a11y.skip")}
      </a>

      <div className="utilitybar">
        <div className="container">
          <div className="utilitybar-left">
            <span className="gov">
              <Icon name="shield" size={14} />
              <b>{t("app.gov")}</b>
            </span>
          </div>
          <div className="utilitybar-right">
            <a className="util-btn" href={`tel:${config.hotline}`}>
              <Icon name="phone" size={14} />
              {t("hotline.label")}: <b>{config.hotline}</b>
            </a>
            <span className="util-sep" aria-hidden="true" />
            <span className="util-sep" aria-hidden="true" />
            <HeaderClock />
            <span className="util-sep" aria-hidden="true" />
            <LangMenu />
            <A11yButton />
          </div>
        </div>
      </div>

      <header className="header">
        <div className="container">
          <Link className="brand" to="/">
            <BrandMark className="brand-mark" spin />
            <span className="brand-text">
              <b>{t("app.name")}</b>
              <span>{t("app.agency")}</span>
            </span>
          </Link>

          <nav className="nav" aria-label={t("action.menu")}>
            {headerNav.map((node) =>
              node.children.length ? (
                <DropdownMenu key={node.key} node={node} />
              ) : (
                <NavLinkItem key={node.key} node={node} />
              ),
            )}
          </nav>

          <div className="header-actions">
            {enter.loggedIn ? (
              <>
                <MobileLinkButton />
                <span className="user-chip" title={enter.user?.email}>
                  <span className="user-chip-dot" aria-hidden="true" />
                  {enter.user?.short_name || enter.user?.full_name}
                </span>
              </>
            ) : null}
            <Link className="btn btn-primary btn-sm" to={enter.to}>
              <Icon name="lock" size={16} />
              {t("action.login")}
            </Link>
            <button
              type="button"
              className="burger"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? t("action.close") : t("action.menu")}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <Icon name={menuOpen ? "close" : "menu"} size={22} />
            </button>
          </div>
        </div>

        <div id="mobile-nav" className={`mobile-nav${menuOpen ? " is-open" : ""}`}>
          <div className="container">
            <MobileNavItems nodes={headerNav} onNavigate={() => setMenuOpen(false)} />
            <Link className="btn btn-primary btn-block" to={enter.to}>
              {t("action.login")}
            </Link>
          </div>
        </div>

        <span
          className="scroll-progress"
          style={{ transform: `scaleX(${progress})` }}
          aria-hidden="true"
        />
      </header>

      <main id="main" className="public-main">
        <ScrollToTop />
        {children ?? <Outlet />}
      </main>

      <PublicFooter />

      <AiAssistant
        agent={{
          public: true,
          pages: assistantPages,
          greeting:
            "Salom! Men Smart Forestry AI yordamchisiman. Platforma, ochiq statistika va xizmatlar haqida so'rang yoki bo'limni ochishni ayting.",
          suggestions: [
            "O'rmon haqida ochiq statistika qanday?",
            "Murojaat qanday yuboriladi?",
            "Xarita bo'limini och",
          ],
        }}
      />
    </div>
  );
}
